/**
 * LeviListeningVisualizer — bounded P0 Tier-2 voice keep-alive surface.
 *
 * PO authorization 2026-08-09 (Tier-2 approved) · P0 2026-08-09 REPAIR-1:
 * consume the VAD's single WebAudio branch instead of creating a second
 * `MediaStreamAudioSourceNode`. The prior implementation created its own
 * `AudioContext` + `MediaStreamAudioSourceNode` on the mic track, which
 * starved the VAD analyser on iOS Safari and produced a false
 * `silence_after_voice` endpoint mid-narration.
 *
 * Purpose:
 *   During ordinary Voice listening turns, iOS Safari's Auto-Lock has
 *   been observed to sleep the display and silently end the microphone
 *   audio track — even while `navigator.wakeLock.request("screen")` is
 *   held. Vision does NOT exhibit this because it attaches a live
 *   camera+audio MediaStream to a visible `<video>` element, which iOS
 *   treats as active media playback.
 *
 *   This component reuses that mechanism WITHOUT activating the camera:
 *   it renders a live audio-level waveform sourced from the VAD's
 *   already-running analyser branch, calls `canvas.captureStream()` to
 *   derive a synthetic video MediaStream, and plays that synthetic
 *   stream in a small visible `<video>` element. iOS sees a page with
 *   an actively-playing video and defers Auto-Lock.
 *
 * Strict boundaries:
 *   - NEVER creates a second AudioContext or MediaStreamAudioSourceNode.
 *   - NEVER requests camera permission.
 *   - NEVER activates the physical camera.
 *   - Synthetic video is NEVER recorded, stored, uploaded, or sent
 *     anywhere. It exists purely as an on-screen media surface.
 *   - Fully torn down when `active` becomes false. Idempotent.
 *
 * UX:
 *   Shows "🎙 Levi is listening · MM:SS" + a small live waveform strip
 *   during listening. The waveform doubles as the on-screen media
 *   surface iOS needs — it is not decorative.
 *
 * Diagnostics:
 *   Publishes `window.__leviVoiceKeepAlive` for Copy Diagnostics.
 */
import { useEffect, useRef, useState } from "react";
import { pushDiag as pushSpeechDiag } from "@/lib/leviSpeech";

function _pd(evt, data) {
  try { pushSpeechDiag(evt, data || {}); } catch { /* noop */ }
}

function _publishState(state) {
  try {
    if (typeof window !== "undefined") {
      window.__leviVoiceKeepAlive = { ...(window.__leviVoiceKeepAlive || {}), ...state };
    }
  } catch { /* noop */ }
}

function _formatElapsed(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function LeviListeningVisualizer({
  active,
  getVadWaveform,
  getVadFftSize,
  lang = "en",
}) {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const rafRef = useRef(0);
  const bufRef = useRef(null);
  const syntheticStreamRef = useRef(null);
  const startedAtRef = useRef(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const elapsedTickRef = useRef(0);

  const _teardown = (endReason) => {
    if (rafRef.current) {
      try { cancelAnimationFrame(rafRef.current); } catch { /* noop */ }
      rafRef.current = 0;
    }
    if (elapsedTickRef.current) {
      try { clearInterval(elapsedTickRef.current); } catch { /* noop */ }
      elapsedTickRef.current = 0;
    }
    try {
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
    } catch { /* noop */ }
    try {
      const ss = syntheticStreamRef.current;
      if (ss && ss.getTracks) {
        ss.getTracks().forEach((t) => { try { t.stop(); } catch { /* noop */ } });
      }
    } catch { /* noop */ }
    syntheticStreamRef.current = null;
    bufRef.current = null;
    _publishState({
      active: false,
      media_element_playing: false,
      synthetic_stream_active: false,
      ended_at: new Date().toISOString(),
      end_reason: endReason || "teardown",
    });
    _pd("voice_keepalive:teardown", { reason: endReason || "teardown" });
  };

  useEffect(() => {
    if (!active) {
      if (syntheticStreamRef.current) {
        _teardown("listening_ended");
      }
      _publishState({
        active: false,
        media_element_playing: false,
        synthetic_stream_active: false,
      });
      setElapsedMs(0);
      return () => {};
    }
    let cancelled = false;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return () => {};

    // Verify the VAD accessor is available. If not, we still render
    // the surface (running rAF on a solid-color canvas suffices to
    // keep captureStream producing frames), but we cannot draw a
    // waveform. Diagnose visibly.
    const fftSize = typeof getVadFftSize === "function" ? getVadFftSize() : 0;
    const canReadWaveform = typeof getVadWaveform === "function" && fftSize > 0;
    if (canReadWaveform) {
      bufRef.current = new Uint8Array(fftSize);
    } else {
      _pd("voice_keepalive:no_vad_waveform", { fftSize });
    }

    // captureStream from the canvas produces the "active playback"
    // signal iOS needs. 30 fps is comfortable and consistent with
    // Vision's video framerate.
    const ctx = canvas.getContext("2d");
    let syntheticStream = null;
    try {
      if (typeof canvas.captureStream === "function") {
        syntheticStream = canvas.captureStream(30);
      } else if (typeof canvas.mozCaptureStream === "function") {
        syntheticStream = canvas.mozCaptureStream(30);
      }
    } catch (err) {
      _pd("voice_keepalive:capturestream_failed", { error: String((err && err.message) || err) });
    }
    if (syntheticStream) {
      syntheticStreamRef.current = syntheticStream;
      try {
        video.srcObject = syntheticStream;
        video.muted = true;
        video.playsInline = true;
        const p = video.play();
        if (p && typeof p.then === "function") p.catch(() => {});
      } catch (err) {
        _pd("voice_keepalive:video_attach_failed", { error: String((err && err.message) || err) });
      }
    } else {
      _pd("voice_keepalive:capturestream_unsupported", {});
    }

    startedAtRef.current = performance.now();
    setElapsedMs(0);
    elapsedTickRef.current = setInterval(() => {
      if (cancelled) return;
      setElapsedMs(performance.now() - startedAtRef.current);
    }, 500);

    _publishState({
      active: true,
      media_element_playing: !!syntheticStream,
      synthetic_stream_active: !!syntheticStream,
      shared_vad_source: true,
      started_at: new Date().toISOString(),
      ended_at: null,
      end_reason: null,
    });
    _pd("voice_keepalive:start", {
      synthetic_stream: !!syntheticStream,
      canvas: !!canvas,
      shared_vad_source: true,
    });

    // Draw loop — reads from the VAD's already-running analyser via
    // the shared accessor. NO second AudioContext. NO second
    // MediaStreamAudioSourceNode.
    const w = canvas.width;
    const h = canvas.height;
    const draw = () => {
      if (cancelled) return;
      try {
        ctx.fillStyle = "#0f172a";
        ctx.fillRect(0, 0, w, h);
        let drewWave = false;
        const buf = bufRef.current;
        if (canReadWaveform && buf) {
          const snap = getVadWaveform(buf);
          if (snap && snap.length === buf.length) {
            ctx.lineWidth = 2;
            ctx.strokeStyle = "#38bdf8";
            ctx.beginPath();
            const n = snap.length;
            const slice = w / n;
            let x = 0;
            for (let i = 0; i < n; i++) {
              const v = snap[i] / 128.0;
              const y = (v * h) / 2;
              if (i === 0) ctx.moveTo(x, y);
              else ctx.lineTo(x, y);
              x += slice;
            }
            ctx.lineTo(w, h / 2);
            ctx.stroke();
            drewWave = true;
          }
        }
        if (!drewWave) {
          // Fallback minimal motion so captureStream still ships fresh
          // frames to iOS Safari when the VAD accessor is temporarily
          // unavailable (e.g., epoch teardown race). Draws a slow
          // pulsing dot so iOS's active-playback heuristic still sees
          // frame changes.
          const t = (performance.now() % 2000) / 2000;
          const r = 3 + 2 * Math.sin(t * Math.PI * 2);
          ctx.fillStyle = "#38bdf8";
          ctx.beginPath();
          ctx.arc(w / 2, h / 2, r, 0, Math.PI * 2);
          ctx.fill();
        }
      } catch { /* single-frame draw error must not kill the loop */ }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelled = true;
      _teardown("effect_cleanup");
    };
    // Intentional: only re-attach on active transition. The accessors
    // are stable ref-readers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    return () => { _teardown("unmount"); };
  }, []);

  if (!active) return null;

  const title = lang === "es" ? "Levi está escuchando" : "Levi is listening";

  return (
    <div
      data-testid="levi-listening-visualizer"
      className="flex items-center gap-3 rounded-md border border-sky-200 bg-slate-900 p-2 text-xs text-white"
      aria-live="polite"
    >
      <span
        aria-hidden="true"
        className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-600"
      >
        <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="font-semibold">{title}</span>
          <span
            data-testid="levi-listening-elapsed"
            className="tabular-nums text-sky-200"
          >
            {_formatElapsed(elapsedMs)}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <canvas
            ref={canvasRef}
            data-testid="levi-listening-canvas"
            width={220}
            height={32}
            className="rounded"
            style={{ display: "block", width: 220, height: 32 }}
          />
          <video
            ref={videoRef}
            data-testid="levi-listening-video"
            muted
            autoPlay
            playsInline
            className="rounded"
            style={{ width: 48, height: 32, display: "block", objectFit: "cover", background: "#0f172a" }}
          />
        </div>
      </div>
    </div>
  );
}
